import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Terminal } from '@xterm/xterm';
import { getCookie } from '../utils/cookies';
import { getSocketUrl, BASE_PATH, authTenantHeaders } from '../services/api';
import { X } from 'lucide-react';
import { Button, IconButton, Input, MonoCell } from '@/components/ds-zta';
import TerminalSurface, { type TerminalSize } from '@/components/terminal/TerminalSurface';
import TerminalChrome from '@/components/terminal/TerminalChrome';
import { connectionStatus } from '@/components/terminal/connectionStatus';

export default function SSHTerminalPage() {
    const xtermRef = useRef<Terminal | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [showConnectionForm, setShowConnectionForm] = useState(true);
    const [user, setUser] = useState('');
    const [searchParams] = useSearchParams();

    const server = searchParams.get('server') || 'unknown';
    const resourceId = searchParams.get('resource_id') || '';
    const defaultUser = searchParams.get('user') || 'root';

    // Initialize default user from query params
    useEffect(() => {
        if (defaultUser && !user) {
            setUser(defaultUser);
        }
    }, [defaultUser]);

    // Connect to WebSocket for the SSH session
    const connectToWebSocket = useCallback((sessionId: string, attempt: number = 1) => {
        const wsUrl = `${getSocketUrl()}/agent/client/${encodeURIComponent(sessionId)}/ws`;

        if (xtermRef.current) {
            xtermRef.current.write(`\r\n\x1b[1;36mConnecting to SSH (attempt ${attempt})...\x1b[0m\r\n`);
        }

        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
            if (xtermRef.current) {
                xtermRef.current.write('\r\n\x1b[1;32mConnected\x1b[0m\r\n\r\n');
            }
            setIsConnected(true);
            setIsConnecting(false);
            setShowConnectionForm(false);
            document.title = `SSH — ${server}`;

            // Application-level heartbeat: proves the browser is alive to the hub so a
            // closed tab is detected and the recording is finalized (WS ping/pong can be
            // forged by proxies, so the server relies on this data message instead).
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            heartbeatRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send('{"type":"client_ping"}');
            }, 10000);
            // Focus terminal so user can type immediately
            setTimeout(() => {
                xtermRef.current?.focus();
                // Send initial terminal size to sync PTY dimensions
                if (xtermRef.current && ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({
                        type: 'resize',
                        cols: xtermRef.current.cols,
                        rows: xtermRef.current.rows,
                    }));
                }
            }, 1000);
        };

        ws.onmessage = async (event) => {
            try {
                let data: string | ArrayBuffer;
                if (event.data instanceof Blob) {
                    data = await event.data.arrayBuffer();
                } else if (event.data instanceof ArrayBuffer) {
                    data = event.data;
                } else {
                    data = event.data;
                }

                let text: string;
                if (data instanceof ArrayBuffer) {
                    text = new TextDecoder().decode(data);
                } else {
                    text = data.toString();
                }

                if (xtermRef.current && text) {
                    xtermRef.current.write(text);
                }
            } catch (err) {
                console.error('Error processing SSH message:', err);
            }
        };

        ws.onclose = () => {
            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
            if (xtermRef.current && isConnected) {
                xtermRef.current.write('\r\n\r\n\x1b[1;31mConnection closed\x1b[0m\r\n');
            }
            setIsConnected(false);
            setIsConnecting(false);
            wsRef.current = null;
        };

        ws.onerror = () => {
            setIsConnecting(false);
            if (attempt < 5) {
                setTimeout(() => {
                    connectToWebSocket(sessionId, attempt + 1);
                }, 2000);
            } else {
                if (xtermRef.current) {
                    xtermRef.current.write('\r\n\x1b[1;31mConnection failed after multiple attempts\x1b[0m\r\n');
                }
            }
        };
    }, [isConnected, server]);

    // Create SSH session via API
    const createSession = useCallback(async (attempt: number = 1) => {
        if (!server || !user?.trim() || !resourceId) return;

        setIsConnecting(true);

        if (xtermRef.current) {
            if (attempt === 1) {
                xtermRef.current.clear();
            }
            xtermRef.current.write(`\x1b[1;36mCreating SSH session to ${server} as ${user}...\x1b[0m\r\n`);
        }

        try {
            const baseUrl = BASE_PATH;
            const response = await fetch(
                `${baseUrl}/agent/ssh/${encodeURIComponent(server)}/resource/${encodeURIComponent(resourceId)}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        // Send the tenant explicitly (as the K8s terminal does) rather
                        // than letting the hub infer it from the Host header — the
                        // access check runs against whichever schema it lands in.
                        ...authTenantHeaders(),
                    },
                    body: JSON.stringify({
                        user: user.trim(),
                        cols: xtermRef.current?.cols || 120,
                        rows: xtermRef.current?.rows || 40,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // A denial comes back as {error, message} — the message carries the
                // actual reason (no approved request, expired, user not provisioned),
                // so show it rather than a bare "Access denied".
                const detail = [errorData.error, errorData.message].filter(Boolean).join(' — ');
                const err: any = new Error(detail || `HTTP ${response.status}`);
                // An authorization failure doesn't heal by itself; retrying it four
                // more times only buries the reason under a scrolling retry loop.
                // 404/503 stay retryable — the hub returns those while an agent is
                // reconnecting, and that does clear on its own.
                err.fatal = [401, 403].includes(response.status);
                throw err;
            }

            const data = await response.json();
            const sessionId = data.session_id;
            sessionIdRef.current = sessionId;
            connectToWebSocket(sessionId, 1);
        } catch (error: any) {
            const errorMessage = error?.message || 'Unknown error';
            if (xtermRef.current) {
                xtermRef.current.write(`\r\n\x1b[1;31mSession error: ${errorMessage}\x1b[0m\r\n`);
            }

            if (!error?.fatal && attempt < 5) {
                if (xtermRef.current) {
                    xtermRef.current.write(`\x1b[90mRetrying in 2 seconds...\x1b[0m\r\n`);
                }
                setTimeout(() => createSession(attempt + 1), 2000);
            } else {
                setIsConnecting(false);
                if (xtermRef.current && !error?.fatal) {
                    xtermRef.current.write('\r\n\x1b[1;31mFailed after multiple attempts\x1b[0m\r\n');
                }
                setShowConnectionForm(true);
            }
        }
    }, [server, user, resourceId, connectToWebSocket]);

    // Handle connect button
    const handleConnect = useCallback(() => {
        if (!user?.trim()) return;
        createSession(1);
    }, [createSession, user]);

    // Handle kill connection
    const handleKillConnection = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
        }

        if (sessionIdRef.current) {
            const token = getCookie('auth_token');
            const baseUrl = BASE_PATH;
            fetch(`${baseUrl}/agent/ssh/${sessionIdRef.current}`, {
                method: 'DELETE',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).catch(() => { });
        }

        if (xtermRef.current) {
            xtermRef.current.clear();
            xtermRef.current.write('\x1b[1;33mConnection terminated. Ready for new connection.\x1b[0m\r\n\r\n');
        }

        sessionIdRef.current = null;
        setIsConnected(false);
        setIsConnecting(false);
        setShowConnectionForm(true);
    }, []);

    // Fire-and-forget terminate that survives page unload (keepalive:true), so closing
    // the tab reliably finalizes the recording server-side (stop + save recording_url →
    // "Play"), instead of leaving the session "active/recording". The hub's terminate
    // handler closes the browser data pump, which runs the recording-finalize cleanup.
    const terminateSession = useCallback(() => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        // navigator.sendBeacon is the ONE request browsers guarantee to flush during
        // page unload (tab close), so it's the reliable finalize signal. Fall back to a
        // keepalive fetch if sendBeacon is unavailable or rejects.
        let sent = false;
        try {
            if (navigator.sendBeacon) sent = navigator.sendBeacon(`${BASE_PATH}/agent/session/${sid}/terminate`);
        } catch { /* ignore */ }
        if (!sent) {
            const token = getCookie('auth_token');
            try {
                fetch(`${BASE_PATH}/agent/ssh/${sid}`, {
                    method: 'DELETE',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    keepalive: true,
                }).catch(() => { });
            } catch { /* ignore */ }
        }
    }, []);

    // Terminate on real tab/window close. pagehide + beforeunload fire on unload (not on
    // tab-switch), so we don't kill a session the user is merely backgrounding.
    useEffect(() => {
        const onUnload = () => terminateSession();
        window.addEventListener('pagehide', onUnload);
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('pagehide', onUnload);
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [terminateSession]);

    // The screen is a TerminalSurface (design eval F40): it owns the xterm
    // instance, fit and resize. This page owns the connection.
    const handleReady = useCallback((term: Terminal) => {
        xtermRef.current = term;
        term.write('\r\n\x1b[1;36mSSH Terminal — ' + server + '\x1b[0m\r\n');
        term.write('\x1b[90mEnter a username and click Connect to start.\x1b[0m\r\n\r\n');
    }, [server]);

    // Pipe terminal input → WebSocket (direct, like K8s terminal)
    const handleData = useCallback((data: string) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(data);
        }
    }, []);

    // Send resize message to sync PTY dimensions
    const handleResize = useCallback(({ cols, rows }: TerminalSize) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
    }, []);

    // Connection teardown when the server changes or the page unmounts.
    useEffect(() => {
        return () => {
            // Finalize the recording on unmount too (covers in-tab teardown).
            terminateSession();
            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [server, terminateSession]);

    const status = connectionStatus(isConnected, isConnecting);

    return (
        // A full-screen route outside the app shell: its own <main> and h1.
        <TerminalChrome
            as="main"
            headingLevel={1}
            className="h-screen w-screen"
            title="SSH Terminal"
            target={server}
            status={status}
            actions={
                <>
                    {isConnected && (
                        <Button variant="danger" size="sm" onClick={handleKillConnection}>
                            Kill & Restart
                        </Button>
                    )}
                    <IconButton
                        aria-label="Close"
                        size="sm"
                        icon={<X />}
                        onClick={() => { terminateSession(); window.close(); }}
                    />
                </>
            }
            toolbar={showConnectionForm && !isConnected && (
                <div className="flex shrink-0 items-end gap-3 border-b border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] px-4 py-2.5">
                    <Input
                        label="Username"
                        size="sm"
                        type="text"
                        value={user}
                        onChange={(e) => setUser(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleConnect()}
                        disabled={isConnecting}
                        className="w-44 [&_input]:font-mono"
                        placeholder="root"
                    />
                    <Button size="sm" onClick={handleConnect} disabled={isConnecting || !user.trim()}>
                        {isConnecting ? 'Connecting…' : 'Connect'}
                    </Button>
                </div>
            )}
            // The connection state is the header's status chip, once (design
            // eval F47: a footer "Ready" beside a header "Disconnected").
            footer={
                <div className="flex items-center gap-4">
                    <span className="font-mono">{server}</span>
                    {sessionIdRef.current && (
                        <span className="flex min-w-0 items-center gap-1">
                            session: <MonoCell value={sessionIdRef.current} width="6rem" />
                        </span>
                    )}
                </div>
            }
        >
            <TerminalSurface
                key={server}
                className="flex-1 p-1"
                fontSize={15}
                canvasRenderer
                aria-label={`SSH terminal for ${server}`}
                onReady={handleReady}
                onDispose={() => { xtermRef.current = null; }}
                onData={handleData}
                onResize={handleResize}
            />
        </TerminalChrome>
    );
}
